import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ADPResourceService, Resource, StorageService } from 'digital-core';
import { AccountService } from 'src/app/core/services/account.service';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { ContractContactDetails, SearchContractRequest, SearchParameter } from 'src/app/model/account/user-otp-request';
import { GTMOnboardingEvents, OnboardingSteps } from 'src/app/model/google-tag-manager/api-status';

@Component({
  selector: 'app-review-contact',
  templateUrl: './review-contact.component.html',
  styleUrls: ['./review-contact.component.scss']
})

export class ReviewContactComponent implements OnInit {
  @Input() contactInfo: ContractContactDetails = new ContractContactDetails();
  @Output() reviewContactResponse = new EventEmitter();

  emailEdit = false;
  phoneEdit = false;
  policyEmailExists= false;
  contactForm: UntypedFormGroup;
  userForm: UntypedFormGroup;
  emailError: string;
  phoneError: string;
  mobilePhoneResourceInfo: Resource;
  maskedPhoneNumber: string;
  isSPOEEnabled: boolean;
  firstNameResourceData: Resource;
  lastNameResourceData: Resource;

  constructor(
    public resourceService: ADPResourceService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private accountService: AccountService,
    private storageService: StorageService
  ) { }

  ngOnInit() {
    this.isSPOEEnabled = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
    this.firstNameResourceData = this.resourceService.getResourceObject('Signup_InvitedUser_ExtraInfo_FirstName_Field');
    this.lastNameResourceData = this.resourceService.getResourceObject('Signup_InvitedUser_ExtraInfo_LastName_Field');
    if (this.contactInfo) {
      this.contactForm = new UntypedFormGroup({
        email: new UntypedFormControl(this.contactInfo.primaryEmail, [Validators.required, Validators.email]),
        phone: new UntypedFormControl(this.contactInfo.phone, [Validators.minLength(10), Validators.maxLength(10)]),
      });
      this.userForm = new UntypedFormGroup({
        firstName: new UntypedFormControl('', [Validators.required]),
        lastName: new UntypedFormControl('', [Validators.required])
      });
      this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.InProcess, OnboardingSteps.ReviewContact);
      this.mobilePhoneResourceInfo = this.getResource('MobileNumber_Field');
      const mobileNumberMaskRegex:string = this.mobilePhoneResourceInfo.MaskRegex;
      if (!!this.contactInfo.phone) {
        if (!!mobileNumberMaskRegex) {
          this.maskedPhoneNumber = this.applyMask(this.contactInfo.phone, mobileNumberMaskRegex);
        } else {
          this.maskedPhoneNumber = this.contactInfo.phone;
        }
      }
      this.setUserForm();
    }
  }

  async setUserForm() {
    if(!this.contactInfo.hasProcessedName) {
     this.userForm.get('firstName').setValue(this.capitalizeFirstLetter(this.contactInfo.firstName));
     this.userForm.get('lastName').setValue(this.capitalizeFirstLetter(this.contactInfo.lastName));
     this.contactInfo.hasProcessedName = true;
    } else {
      this.userForm.get('firstName').setValue(this.contactInfo.firstName);
      this.userForm.get('lastName').setValue(this.contactInfo.lastName);
    }
  }

  capitalizeFirstLetter(str: string): string {
    if (str.length === 0) {
      return ""; // Handle empty string case
    }
    const lowerCaseStr = str.toLowerCase(); // Convert the entire string to lowercase first
    return lowerCaseStr.charAt(0).toUpperCase() + lowerCaseStr.slice(1); // Capitalize the first letter and concatenate it with the rest of the lowercase string
  }


  applyMask(value: string, mask: string, maskChar: string = '*'): string {
    let result = '';
    let valueIndex = 0;

    for (let maskIndex = 0; maskIndex < mask.length; maskIndex++) {
      const maskCharacter = mask[maskIndex];

      if (maskCharacter === '0') {
        if (valueIndex < value.length) {
          result += value[valueIndex];
          valueIndex++;
        } else {
          result += maskChar;
        }
      } else if (maskCharacter === '#') {
          if (valueIndex < value.length) {
            if (!isNaN(Number(value[valueIndex]))) {
              result += value[valueIndex];
              valueIndex++;
            } else {
              result += maskChar;
            }
          } else {
            result += maskChar;
          }
      } else {
        result += maskCharacter;
      }
    }

    return result;
  }

  confirm() {
    this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.ReviewContact);
    this.contactInfo.firstName = this.userForm.get('firstName').value;
    this.contactInfo.lastName = this.userForm.get('lastName').value;
    if (this.contactForm.dirty) {
      const updatedEmail = this.contactForm.get('email').value.toLowerCase();

      // Only validate email if it has changed
      if (updatedEmail !== this.contactInfo.primaryEmail) {
        this.validateEmailUniqueness(updatedEmail);
        this.storageService.setSession('RegEmail', updatedEmail);

      } else {
        this.storageService.setSession('RegEmail', updatedEmail);
        this.contactInfo.primaryEmail = updatedEmail;
        this.contactInfo.phone = this.contactForm.get('phone').value;
        this.reviewContactResponse.emit(this.contactInfo);
      }
    } else {
      // check if the email on policy alreaddy exists in okta
      this.accountService.checkOktaExists(this.contactInfo.primaryEmail).subscribe({
        next: (response: any) => {
          if (response) {
            this.policyEmailExists = true;
            this.emailError = this.resourceService.getViewLabel('Global_Error_Code_OktaUserAlreadyExist');
          } else {
            this.storageService.setSession('RegEmail', this.contactInfo.primaryEmail);
            this.reviewContactResponse.emit(null);
          }
        }
      });
    }
  }

  validateEmailUniqueness(email: string) {
    const searchParameter = new SearchParameter('Email', email);
    const searchRequest = new SearchContractRequest();
    searchRequest.reCaptchaValue = '';
    searchRequest.searchParameters = [searchParameter];

    this.accountService.checkOktaExists(email).subscribe({
      next: (response: any) => {
        if (response) {
          this.emailError = this.resourceService.getViewLabel('Global_Error_Code_OktaUserAlreadyExist');
        } else {
          this.contactInfo.primaryEmail = email;
          this.storageService.setSession('RegEmail', this.contactInfo.primaryEmail);
          this.contactInfo.phone = this.contactForm.get('phone').value;
          this.reviewContactResponse.emit(this.contactInfo);
        }
      },
      error: (err) => {
        throw err;
      }
    });
  }

  onBlurEmailField() {
    if (!this.contactForm.get('email').value) {
      this.validateEmailRequired();
    } else {
      this.validateEmailFormat();
    }
  }

  validateEmailRequired() {
    this.emailError = !this.contactForm.get('email').value ? this.resourceService.getViewLabel('Global_Error_Required_Email') : undefined;
  }

  validateEmailFormat() {
    const resource = this.getResource('EmailAddress_Field');
    const isValid = !!String(this.contactForm.get('email').value).toLowerCase().match(resource.ValidationRegex);
    this.emailError = !isValid ? this.resourceService.getViewLabel('Global_Error_Valid_Email') : undefined;
  }

  onBlurPhoneField() {
    if (this.contactForm.get('phone').value) {
      this.validateInputFieldFormat();
    } else {
      this.phoneError = undefined;
    }
  }

  validateInputFieldRequired() {
    this.phoneError = !this.contactForm.get('phone').value ? this.resourceService.getViewLabel('Global_Error_Required_Phone_No') : undefined;
  }

  validateInputFieldFormat() {
    const optionValue = this.contactForm.get('phone').value;
    const isValid = !!(String(optionValue).length == 10);
    this.phoneError = !isValid ? this.resourceService.getViewLabel('Global_Error_Valid_Phone_No') : undefined;
  }

  getResource(name: string, namespace = null) {
    namespace = namespace ? namespace : "Signup_Create_Account";
    const title = namespace + "_" + name;
    const resourceValue = this.resourceService.getViewResourceObject(title);
    return resourceValue;
  }

}
